import { FC, useContext } from "react";
import Select, { GroupBase, OptionsOrGroups, PropsValue } from "react-select";
import DatePicker from "react-datepicker";

import { QueryStateActions } from "src/pages/dashboard/types";
import { CaseFilterProps } from "./types";
import useDrugNameListing from "src/hooks/useDrugNameListing";
import { format, parse } from "date-fns";
import { offset, flip, shift, limitShift } from "@floating-ui/react";
import { isZilrettaUser } from "src/helpers";
import { AuthContext } from "src/context/authContext";


const TableFilters: FC<CaseFilterProps> = ({ state, dropdownData, inputPlaceholder, dispatch, handleSearchWithQueryParam, onClearButtonClicked, onStatusChange }): JSX.Element => {
  const { userData } = useContext(AuthContext)
  const { productNameListing } = useDrugNameListing();

  const handleDateFromChange = (date: Date | null): void => {
    dispatch({
      type: QueryStateActions.CHANGE_FROM_DATE,
      payload: date ? format(date, "MM/dd/yyyy") : "",
    });
  };

  const handleDateToChange = (date: Date | null) => {
    dispatch({
      type: QueryStateActions.CHANGE_TO_DATE,
      payload: date ? format(date, "MM/dd/yyyy") : "",
    });
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: QueryStateActions.CHANGE_SEARCH_QUERY,
      payload: e.target.value
    });
  };

  const parseSearchQueries = () => {
    dispatch({ type: QueryStateActions.CHANGE_PAGE, payload: 1, });
    const updatedState = { ...state };
    if (state.date_from && state.date_from.length > 0)
      updatedState.date_from = format(parse(state.date_from, "MM/dd/yyyy", new Date()), "yyyy-MM-dd")
    if (state.date_to && state.date_to.length > 0)
      updatedState.date_to = format(parse(state.date_to, "MM/dd/yyyy", new Date()), "yyyy-MM-dd")
    console.log({ updatedState })
    handleSearchWithQueryParam({ ...updatedState, page: 1 });
  }

  const onEnterPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      parseSearchQueries()
    }
  }

  const handleDrugChange = (val: { label: string; value: string }) => {
    dispatch({
      type: QueryStateActions.CHANGE_DRUG,
      payload: val.value,
    });
  };

  const updateCaseFilters = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    parseSearchQueries()
  }

  return (
      <form onSubmit={updateCaseFilters} className="row g-2 statistics-filter" data-tour="third-step">
          <div className="col date-col">
              <Select
                  options={dropdownData as unknown as OptionsOrGroups<string | boolean, GroupBase<string | boolean>>}
                  noOptionsMessage={() => ""}
                  onChange={(val) => {
                      const selectedValue = val as unknown as { label: string; value: string | boolean };
                      (typeof selectedValue.value === "boolean") ?
                          dispatch({
                              type: QueryStateActions.CHANGE_CASE_STATUS,
                              payload: selectedValue.value,
                          }) :
                          dispatch({
                              type: QueryStateActions.CHANGE_CATEGORY_TYPE,
                              payload: selectedValue.value,
                          });
                      onStatusChange(selectedValue.label);
                  }}
                  isSearchable={false}
                  value={((openValue: string | boolean) => {
                      const data = dropdownData.find(singleStatus =>
                          singleStatus.value === openValue
                      );
                      if (data) return data as unknown as PropsValue<string | boolean>;
                  })(typeof state.open === "boolean" ? state.open : state.category_type)}
              />
          </div>
          <div className="col date-col">
              {userData && isZilrettaUser(userData) ? (
                  <span className="fs-6">ZILRETTA</span>
              ) : (
                  <Select
                      options={
                          [
                              {value: "", label: "Select Product"},
                              ...(productNameListing
                                  ? productNameListing
                                      .map((item) => ({
                                          value: item.name.toLowerCase(),
                                          label:
                                              item.name.toLowerCase() === "zilretta"
                                                  ? item.name.toUpperCase()
                                                  : item.name,
                                      }))
                                      .map((procedure) => ({
                                          value: procedure.value,
                                          label: procedure.label,
                                      }))
                                  : []),
                          ] as unknown as OptionsOrGroups<string, GroupBase<string>>
                      }
                      noOptionsMessage={() => ""}
                      onChange={(val) => {
                          const selectedValue = val as unknown as { label: string; value: string };
                          handleDrugChange(selectedValue);
                      }}
                      isSearchable={false}
                      value={((drug_name: string) => {
                          if (drug_name === "") return null;
                          const new_drug =
                              productNameListing &&
                              productNameListing
                                  .map((item) => ({
                                      value: item.name.toLowerCase(),
                                      label:
                                          item.name.toLowerCase() === "zilretta"
                                              ? item.name.toUpperCase()
                                              : item.name,
                                  }))
                                  .find((option) => option.value === drug_name);

                          return new_drug as unknown as PropsValue<string>;
                      })(state.drug_name)}
                      placeholder="Select Product..."
                  />
              )}
          </div>
          <div className="col date-col">
              <DatePicker
                  selected={state.date_from ? new Date(state.date_from) : null} // Ensure correct date format
                  onChange={(date) => handleDateFromChange(date)} // Pass Date | null directly
                  className="form-control"
                  wrapperClassName="w-100"
                  placeholderText="mm/dd/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  maxDate={state.date_to ? new Date(state.date_to) : undefined}
                  isClearable
                  portalId="root"
                  popperModifiers={[offset(8), shift({limiter: limitShift()})]}
              />
          </div>
          <div className="col date-col">
              <DatePicker
                  selected={state.date_to ? new Date(state.date_to) : null} // Use selected instead of value
                  onChange={(date) => handleDateToChange(date)} // Pass Date | null directly
                  className="form-control"
                  wrapperClassName="w-100"
                  placeholderText="mm/dd/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  minDate={state.date_from ? new Date(state.date_from) : undefined}
                  isClearable
                  portalId="root"
                  popperModifiers={[offset(8), shift({limiter: limitShift()})]}
              />
          </div>
          <div className="col patient-col">
              <input type="text" onChange={handleSearchQueryChange} value={state.search_query}
                     placeholder={inputPlaceholder} className="form-control" onKeyDown={onEnterPress}/>
          </div>
          <div className="col date-col d-flex gap-2">
              <button type="submit" className="btn btn-primary w-100">Find</button>
              <button type="button" onClick={onClearButtonClicked} className="btn btn-outline-secondary w-100">Clear</button>
          </div>
      </form>
  );
};

export default TableFilters;
